/**
 * LIVE TARGETING PICKERS for the YouTube builders.
 *
 * Google targets locations and languages by CONSTANT ID, never by name — so
 * both pickers search Google live (geoTargetConstants:suggest for places, the
 * cached language_constant table for languages) and hold the chosen rows as
 * chips. Debounced 350ms, min two characters, last-response-wins so a slow
 * earlier query can't overwrite a newer one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/Input';
import { LANGUAGES as COMMON_LANGUAGES } from '@/pages/google-ads/googleAdsTargeting';
import { googleAdsService, GoogleAdsGeoSuggestion, GoogleAdsLanguage } from '@/services/google-ads.service';
import { Checkbox, errMsg, Spinner } from './youtubeShared';

const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;

export const Chip = ({ label, sub, onRemove }: { label: string; sub?: string; onRemove: () => void }) => (
  <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-surface-alt px-2.5 py-0.5 pr-1 text-xs text-text-primary">
    <span className="truncate">{label}</span>
    {sub && <span className="shrink-0 text-2xs text-text-muted">{sub}</span>}
    <button type="button" className="shrink-0 rounded-full p-0.5 hover:bg-surface" onClick={onRemove} aria-label={`Remove ${label}`}>
      <X size={12} />
    </button>
  </span>
);

/** Debounced remote search with last-response-wins. */
function useLiveSearch<T>(fetcher: (q: string) => Promise<T[]>) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const seq = useRef(0);

  useEffect(() => () => clearTimeout(timer.current), []);

  const onQuery = useCallback((q: string) => {
    setQuery(q);
    clearTimeout(timer.current);
    setError(null);
    if (q.trim().length < MIN_CHARS) { setResults([]); setBusy(false); return; }
    timer.current = setTimeout(async () => {
      const mine = ++seq.current;
      setBusy(true);
      try {
        const rows = await fetcher(q.trim());
        if (mine === seq.current) setResults(rows);
      } catch (e) {
        if (mine === seq.current) { setResults([]); setError(errMsg(e, 'Search failed.')); }
      } finally {
        if (mine === seq.current) setBusy(false);
      }
    }, DEBOUNCE_MS);
  }, [fetcher]);

  const reset = useCallback(() => { clearTimeout(timer.current); seq.current++; setQuery(''); setResults([]); setBusy(false); }, []);

  return { query, results, busy, error, onQuery, reset };
}

// ── locations ────────────────────────────────────────────────────────────

export function LocationSearch({ accountUid, value, onChange, placeholder, countryCode }: {
  accountUid: string;
  value: GoogleAdsGeoSuggestion[];
  onChange: (next: GoogleAdsGeoSuggestion[]) => void;
  placeholder?: string;
  /** ISO-2 — narrows "Springfield" to one country's Springfields. */
  countryCode?: string;
}) {
  const fetcher = useCallback(
    (q: string) => googleAdsService.suggestGeo(accountUid, q, countryCode),
    [accountUid, countryCode],
  );
  const { query, results, busy, error, onQuery, reset } = useLiveSearch(fetcher);
  const chosen = new Set(value.map((l) => l.id));
  const visible = results.filter((r) => !chosen.has(r.id)).slice(0, 10);

  return (
    <div>
      {value.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {value.map((l) => (
            <Chip key={l.id} label={l.name} sub={l.canonicalName && l.canonicalName !== l.name ? l.canonicalName : undefined}
              onRemove={() => onChange(value.filter((x) => x.id !== l.id))} />
          ))}
        </div>
      )}
      <Input
        iconLeft={<Search size={14} />}
        iconRight={busy ? <Spinner size={14} /> : undefined}
        placeholder={placeholder ?? 'e.g. United Arab Emirates, Dubai, Riyadh…'}
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        aria-label="Search locations"
      />
      {error && <p className="mt-2 break-words text-xs text-danger">{error}</p>}
      {!error && !busy && query.trim().length >= MIN_CHARS && visible.length === 0 && (
        <p className="mt-2 text-2xs text-text-muted">No locations match "{query.trim()}".</p>
      )}
      {visible.length > 0 && (
        <div className="mt-1 max-h-64 overflow-auto rounded-xl border border-border bg-surface">
          {visible.map((l) => (
            <button key={l.id} type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary hover:bg-surface-alt"
              onClick={() => { onChange([...value, l]); reset(); }}>
              <span className="min-w-0 flex-1 truncate">
                {l.name}
                {l.canonicalName && l.canonicalName !== l.name && (
                  <span className="ml-1.5 text-2xs text-text-muted">{l.canonicalName}</span>
                )}
              </span>
              {l.targetType && <span className="shrink-0 text-[10px] uppercase text-text-muted">{l.targetType.replace(/_/g, ' ')}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── languages ────────────────────────────────────────────────────────────

export function LanguageSearch({ accountUid, value, onChange, placeholder }: {
  accountUid: string;
  value: GoogleAdsLanguage[];
  onChange: (next: GoogleAdsLanguage[]) => void;
  placeholder?: string;
}) {
  const fetcher = useCallback((q: string) => googleAdsService.listLanguages(accountUid, q), [accountUid]);
  const { query, results, busy, error, onQuery, reset } = useLiveSearch(fetcher);
  const chosen = new Set(value.map((l) => l.id));
  const visible = results.filter((r) => !chosen.has(r.id)).slice(0, 10);

  return (
    <div>
      {value.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {value.map((l) => (
            <Chip key={l.id} label={l.name} sub={l.code} onRemove={() => onChange(value.filter((x) => x.id !== l.id))} />
          ))}
        </div>
      )}
      <Input
        iconLeft={<Search size={14} />}
        iconRight={busy ? <Spinner size={14} /> : undefined}
        placeholder={placeholder ?? 'e.g. English, Arabic, Urdu…'}
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        aria-label="Search languages"
      />
      {error && <p className="mt-2 break-words text-xs text-danger">{error}</p>}
      {!error && !busy && query.trim().length >= MIN_CHARS && visible.length === 0 && (
        <p className="mt-2 text-2xs text-text-muted">No languages match "{query.trim()}".</p>
      )}
      {visible.length > 0 && (
        <div className="mt-1 max-h-64 overflow-auto rounded-xl border border-border bg-surface">
          {visible.map((l) => (
            <button key={l.id} type="button"
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-text-primary hover:bg-surface-alt"
              onClick={() => { onChange([...value, l]); reset(); }}>
              <span className="truncate">{l.name}</span>
              {l.code && <span className="shrink-0 text-[10px] uppercase text-text-muted">{l.code}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── languages: common grid + search ──────────────────────────────────────

/**
 * WhisperCRM's language step is a checkbox grid of the languages MENA
 * advertisers actually pick; SlightorCRM keeps that grid AND adds the live
 * search underneath for everything else. Both write to the same chip list —
 * a language ticked in the grid and one found by search are the same thing
 * (a `languageConstants/{id}`). Empty = all languages.
 */
export function LanguagePicker({ accountUid, value, onChange }: {
  accountUid: string;
  value: GoogleAdsLanguage[];
  onChange: (next: GoogleAdsLanguage[]) => void;
}) {
  const chosen = new Set(value.map((l) => String(l.id)));
  const toggle = (id: number, label: string) => {
    const key = String(id);
    if (chosen.has(key)) onChange(value.filter((l) => String(l.id) !== key));
    else onChange([...value, { id: key, name: label, targetable: true }]);
  };
  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 text-2xs font-medium uppercase tracking-wide text-text-muted">Common languages</div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {COMMON_LANGUAGES.map((l) => (
            <label key={l.id} className="flex items-center gap-2 text-sm text-text-primary">
              <Checkbox checked={chosen.has(String(l.id))} onCheckedChange={() => toggle(l.id, l.label)} />
              {l.label}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="mb-2 text-2xs font-medium uppercase tracking-wide text-text-muted">Any other language</div>
        <LanguageSearch accountUid={accountUid} value={value} onChange={onChange} placeholder="Search Google's full language list…" />
      </div>
    </div>
  );
}
